import { useState, useEffect } from 'react'
import { User, Mail, Lock, AlertTriangle, Check, Eye, EyeOff, Rss, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { dashboardApi } from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DefaultPublication } from './default-publication'

export function DashboardAccount() {
  const queryClient = useQueryClient()

  // Fetch user profile
  const { data: userData, isLoading } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await dashboardApi.getProfile()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load profile')
    }
  })

  const [profileData, setProfileData] = useState({
    first_name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Update profile data when userData loads
  useEffect(() => {
    if (userData) {
      setProfileData(prev => ({
        ...prev,
        first_name: userData.first_name || '',
        email: userData.email || ''
      }))
    }
  }, [userData])

  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [defaultHomeTab, setDefaultHomeTab] = useState(() => {
    const saved = localStorage.getItem('defaultHomeTab')
    return saved === 'feed' || saved === 'firehose' ? saved : 'feed'
  })

  // Save default home tab preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('defaultHomeTab', defaultHomeTab)
  }, [defaultHomeTab])

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data) => dashboardApi.updateProfile(data),
    onSuccess: () => {
      toast.success('Profile updated successfully!')
      // Invalidate and refetch user profile
      queryClient.invalidateQueries({ queryKey: ['userProfile'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update profile')
    }
  })

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data) => dashboardApi.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully!')
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }))
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'Failed to change password'
      if (errorMsg === 'INVALID_OLD_PASSWORD') {
        toast.error('Current password is incorrect')
      } else if (errorMsg === 'BOTH_PASSWORD_REQUIRED') {
        toast.error('Both passwords are required')
      } else {
        toast.error(errorMsg)
      }
    }
  })

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: () => dashboardApi.deleteAccount(),
    onSuccess: () => {
      toast.success('Account deleted successfully')
      // Clear auth and redirect to home
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.href = '/'
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete account')
    }
  })

  const handleProfileUpdate = (e) => {
    e.preventDefault()
    // Only send first_name, email is not editable
    updateProfileMutation.mutate({
      first_name: profileData.first_name
    })
  }

  const handlePasswordChange = (e) => {
    e.preventDefault()

    if (profileData.newPassword !== profileData.confirmPassword) {
      toast.error('Passwords do not match!')
      return
    }

    if (profileData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters!')
      return
    }

    if (!profileData.currentPassword) {
      toast.error('Current password is required!')
      return
    }

    changePasswordMutation.mutate({
      old_password: profileData.currentPassword,
      new_password: profileData.newPassword
    })
  }

  const handleDeleteAccount = () => {
    deleteAccountMutation.mutate()
    setDeleteDialogOpen(false)
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <User className="w-8 h-8 text-primary" />
          Account Settings
        </h1>
        <p className="text-gray-600">Manage your account information and security</p>
      </div>

      <div className="space-y-6">
        {/* Profile Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div data-logo-box className="bg-gradient-to-r from-slate-700 to-slate-800 p-6 transition-all duration-300">
            <div className="flex items-center gap-3 text-white">
              <User className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Profile Information</h2>
                <p className="text-slate-100 text-sm">Update your personal details</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleProfileUpdate} className="p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={profileData.first_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  placeholder="your.email@example.com"
                  className="pl-10 bg-gray-50 cursor-not-allowed"
                  disabled
                  readOnly
                />
              </div>
              <p className="text-sm text-gray-500">
                Email address cannot be changed. Contact support if you need to update it.
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="gap-2" disabled={updateProfileMutation.isLoading}>
                <Check className="w-4 h-4" />
                {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
            <div className="flex items-center gap-3 text-white">
              <Lock className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Change Password</h2>
                <p className="text-purple-100 text-sm">Update your password regularly for security</p>
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={profileData.currentPassword}
                  onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={profileData.newPassword}
                  onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Must be at least 8 characters long
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={profileData.confirmPassword}
                  onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="gap-2" disabled={changePasswordMutation.isLoading}>
                <Check className="w-4 h-4" />
                {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Default Home Tab */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
            <div className="flex items-center gap-3 text-white">
              <Rss className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Default Home Tab</h2>
                <p className="text-blue-100 text-sm">Choose which tab is active when you log in</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant={defaultHomeTab === 'feed' ? 'default' : 'outline'}
                onClick={() => setDefaultHomeTab('feed')}
                className="flex-1 gap-2"
              >
                <Rss className="w-4 h-4" />
                Feed
              </Button>
              <Button
                type="button"
                variant={defaultHomeTab === 'firehose' ? 'default' : 'outline'}
                onClick={() => setDefaultHomeTab('firehose')}
                className="flex-1 gap-2"
              >
                <Zap className="w-4 h-4" />
                Firehose
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Default Publication */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <DefaultPublication />
        </motion.div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border-2 border-red-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-6">
            <div className="flex items-center gap-3 text-white">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Danger Zone</h2>
                <p className="text-red-100 text-sm">Irreversible actions</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Delete Account</h3>
                <p className="text-sm text-gray-600">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteAccountMutation.isLoading}
              >
                {deleteAccountMutation.isLoading ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
              <br /><br />
              Are you absolutely sure you want to delete your account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Delete My Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
